// src/hooks/useDentallyLeadSync.js
//
// One practice/lead-scoped answer to "does the CRM record differ from the linked
// Dentally patient, and may I copy the Dentally values across?".
//
// Rules this hook exists to enforce:
//   - It never throws and never surfaces an error to the page. A failed check is
//     a quiet state (`providerError`), not a page-level error.
//   - Pass a null practiceId/leadId to disable it entirely: no request is made.
//     The caller uses this so the panel costs nothing on a lead with no link.
//   - A stale response from a previous practice/lead can never land on the
//     current one (generation counter, same pattern as useDentallyPatientLink).
//   - Every apply attempt carries a NEW idempotency key. Retrying after a failure
//     is a new attempt, not a replay of the previous one.
import { useCallback, useEffect, useRef, useState } from 'react';
import { applyDentallyLeadSync, fetchDentallyLeadSync, pushDentallyLeadDetails } from '../utils/dentallyLeadSyncApi';
import newIdempotencyKey from '../utils/idempotencyKey';
import { usableDifferences } from '../utils/dentallyLeadSyncPresentation';
import { normalisePatientSummary } from '../utils/dentallyPatientSummary';

const EMPTY = Object.freeze([]);

const normaliseProviderError = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return { detail: value, code: null };
  if (typeof value !== 'object') return { detail: '', code: null };
  return { detail: typeof value.detail === 'string' ? value.detail : '', code: value.code ?? null };
};

/** Field slugs out of an `applied` list that may be slugs or objects. */
const appliedFields = (applied) => (Array.isArray(applied) ? applied : [])
  .map((entry) => (typeof entry === 'string' ? entry : entry?.field))
  .filter(Boolean);

/**
 * Map a failed apply onto a code the UI can word plainly. A 409 carries its own
 * code; 403 and the provider statuses do not, so they are named here.
 */
const failureCodeFor = (status, data) => {
  if (status === 403) return 'forbidden';
  if (status === 429 || status === 502 || status === 503) return 'provider_unavailable';
  return typeof data?.code === 'string' ? data.code : null;
};

/**
 * The CRM -> Dentally half of the payload.
 *
 * `state` is the durable push state (SYNCED / PENDING / IN_PROGRESS /
 * CONFLICT / UNKNOWN). CONFLICT is the one the UI must act on: it means the
 * practice edited the patient in Dentally too, so the push wrote nothing and
 * a human has to choose. `identityPending` names the identity fields waiting
 * on an explicit confirmation — without it the UI cannot offer one.
 */
const normalisePush = (value) => {
  if (!value || typeof value !== 'object') return null;
  return {
    state: typeof value.state === 'string' ? value.state : null,
    errorCode: typeof value.error_code === 'string' ? value.error_code : null,
    lastSyncedAt: value.last_synced_at ?? null,
    identityPending: Array.isArray(value.identity_pending) ? value.identity_pending.filter(Boolean) : [],
  };
};

const normaliseSkipped = (skipped) => (Array.isArray(skipped) ? skipped : [])
  .map((entry) => (typeof entry === 'string' ? { field: entry, reason: null } : entry))
  .filter((entry) => entry && entry.field);

export default function useDentallyLeadSync(practiceId, leadId) {
  const enabled = Boolean(practiceId && leadId);
  const [state, setState] = useState({
    status: 'idle', linked: false, patientId: null, checkedAt: null, differences: EMPTY, canApply: false, providerError: null, blockedReason: null, patientSummary: null, push: null,
  });
  const [isApplying, setIsApplying] = useState(false);
  const [isPushing, setIsPushing] = useState(false);
  // { appliedFields, skipped, archived, failed } — the inline receipt after an apply.
  const [applyOutcome, setApplyOutcome] = useState(null);
  const [isArchived, setIsArchived] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const generationRef = useRef(0);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    setApplyOutcome(null);
    if (!enabled) {
      setState({ status: 'idle', linked: false, patientId: null, checkedAt: null, differences: EMPTY, canApply: false, providerError: null, blockedReason: null, patientSummary: null, push: null });
      return undefined;
    }
    setState((prev) => ({ ...prev, status: 'loading' }));

    fetchDentallyLeadSync(practiceId, leadId)
      .then((response) => {
        if (generation !== generationRef.current) return;
        const data = response?.data;
        // 403 (no view access to the feature) and 404 (lead outside this
        // practice) are not Dentally being unreachable — saying so would be
        // untrue. Render nothing instead: the panel has no business appearing
        // for a lead this user cannot see. Everything else non-200 is a genuine
        // "we couldn't check", and is never read as "nothing differs".
        if (response?.status === 403 || response?.status === 404) {
          setState({
            status: 'loaded', linked: false, patientId: null, checkedAt: null, differences: EMPTY, canApply: false, patientSummary: null, push: null,
            providerError: null, blockedReason: null,
          });
          return;
        }
        if (response?.status !== 200 || !data || typeof data !== 'object') {
          setState({
            status: 'loaded', linked: false, patientId: null, checkedAt: null, differences: EMPTY, canApply: false, patientSummary: null, push: null,
            providerError: { detail: '', code: 'unavailable' }, blockedReason: null,
          });
          return;
        }
        setState({
          status: 'loaded',
          linked: data.linked !== false,
          patientId: data.dentally_patient_id ?? null,
          checkedAt: data.checked_at ?? null,
          differences: usableDifferences(data.differences),
          canApply: data.can_apply === true,
          providerError: normaliseProviderError(data.provider_error),
          blockedReason: typeof data.blocked_reason === 'string' ? data.blocked_reason : null,
          // Read-only provider facts for the Dentally patient card. Null when
          // the lead is unlinked or the provider read failed, in which case the
          // card renders no summary rather than a row of blanks.
          patientSummary: normalisePatientSummary(data.patient_summary),
          push: normalisePush(data.push),
        });
      })
      .catch(() => {
        if (generation !== generationRef.current) return;
        setState({
          status: 'loaded', linked: false, patientId: null, checkedAt: null, differences: EMPTY, canApply: false, patientSummary: null, push: null,
          providerError: { detail: '', code: 'unavailable' }, blockedReason: null,
        });
      });

    return () => { generationRef.current += 1; };
  }, [practiceId, leadId, enabled, reloadKey]);

  /**
   * Copy the ticked Dentally values into the CRM.
   *
   * Resolves to { ok, archived, failureCode, appliedFields, skipped } — the
   * caller refreshes the lead in Redux when `ok` and something was applied.
   *
   * Failure codes come straight from the contract's POST error responses
   * (409 lead_archived / not_linked / integration_unavailable /
   * idempotency_key_conflict, 403, and the 429/502/503 provider shape). The
   * server writes nothing on any of them, so every failure is safe to retry —
   * and a retry is a genuinely new attempt with a new key.
   */
  const apply = useCallback(async (fields) => {
    const wanted = (Array.isArray(fields) ? fields : []).filter(Boolean);
    if (!enabled || wanted.length === 0) {
      return { ok: false, archived: false, failureCode: null, appliedFields: [], skipped: [] };
    }
    const generation = generationRef.current;
    setIsApplying(true);
    setApplyOutcome(null);
    let result = { ok: false, archived: false, failureCode: null, appliedFields: [], skipped: [] };
    try {
      const response = await applyDentallyLeadSync(practiceId, leadId, {
        idempotencyKey: newIdempotencyKey(),
        fields: wanted,
      });
      const data = response?.data;
      const status = response?.status;
      if (status === 200 && data && typeof data === 'object') {
        result = {
          ok: true,
          archived: false,
          failureCode: null,
          appliedFields: appliedFields(data.applied),
          skipped: normaliseSkipped(data.skipped),
          differences: usableDifferences(data.differences),
          checkedAt: data.checked_at ?? null,
          patientId: data.dentally_patient_id ?? null,
        };
      } else {
        result = { ...result, failureCode: failureCodeFor(status, data) };
        result.archived = result.failureCode === 'lead_archived';
      }
    } catch {
      result = { ok: false, archived: false, failureCode: null, appliedFields: [], skipped: [] };
    }
    if (generation === generationRef.current) {
      setIsApplying(false);
      setIsArchived(result.archived);
      setApplyOutcome({
        appliedFields: result.appliedFields,
        skipped: result.skipped,
        archived: result.archived,
        failureCode: result.failureCode,
        failed: !result.ok && !result.archived,
      });
      if (result.ok) {
        setState((prev) => ({
          ...prev,
          differences: result.differences ?? EMPTY,
          checkedAt: result.checkedAt ?? prev.checkedAt,
          patientId: result.patientId ?? prev.patientId,
        }));
      }
    } else {
      setIsApplying(false);
    }
    return result;
  }, [enabled, practiceId, leadId]);

  /**
   * Send the CRM's current detail values TO Dentally.
   *
   * Called after a successful save of Personal details, and again from the
   * confirmation when the operator agrees to change the name or date of
   * birth. Resolves to { ok, outcome, fields, identityPending, conflict }.
   *
   * Never rejects and never raises a page-level error: a lead must stay
   * editable when Dentally is unreachable. A failed push leaves the row
   * PENDING server-side and the sweeper retries it, so the honest reading of
   * a failure here is "not yet", not "lost".
   */
  const push = useCallback(async ({ confirmIdentity = false } = {}) => {
    if (!enabled) {
      return { ok: false, outcome: null, fields: [], identityPending: [], conflict: false };
    }
    const generation = generationRef.current;
    setIsPushing(true);
    try {
      const response = await pushDentallyLeadDetails(practiceId, leadId, { confirmIdentity });
      const data = response?.data;
      const outcome = typeof data?.outcome === 'string' ? data.outcome : null;
      const pushState = normalisePush(data?.push);
      if (generation === generationRef.current && pushState) {
        setState((prev) => ({ ...prev, push: pushState }));
      }
      return {
        ok: response?.status === 200 && outcome === 'synced',
        outcome,
        fields: Array.isArray(data?.fields) ? data.fields : [],
        identityPending: pushState?.identityPending ?? [],
        conflict: outcome === 'conflict',
      };
    } catch (error) {
      return { ok: false, outcome: 'unavailable', fields: [], identityPending: [], conflict: false };
    } finally {
      setIsPushing(false);
    }
  }, [enabled, practiceId, leadId]);

  return {
    enabled,
    isLoading: state.status === 'loading',
    loaded: state.status === 'loaded',
    linked: state.linked,
    patientId: state.patientId,
    checkedAt: state.checkedAt,
    patientSummary: state.patientSummary,
    differences: state.differences,
    canApply: state.canApply,
    blockedReason: state.blockedReason,
    providerError: state.providerError,
    isApplying,
    isArchived,
    applyOutcome,
    // CRM -> Dentally
    push,
    isPushing,
    pushState: state.push,
    apply,
    refresh,
  };
}
